import React, { useState } from "react";

import { stepCompleted } from "EmployeeApplication/actions";
import { getDependents, getEmployeeApplication, getEmployeeForm, useUpdateEmployeePolicyMutation } from "EmployeeApplication/api";
import { useDispatch, useSelector } from "react-redux";
import formatCurrency from "Utils/formatCurrency";
import { formatNameFL } from "Utils/formatName";

import BodyText from "Cobalt/BodyText";
import Button from "Cobalt/Button";
import { CardSection } from "Cobalt/Card";
import { HeaderTR, Table, TD, TH, TR } from "Cobalt/TableV2";
import CarrierLogo from "Common/CarrierLogo";
import { Li, Span, Ul } from "Common/HTML";
import Icon from "Common/Icon";
import InfoPopover from "Common/InfoPopover";

import { AccidentEnrollOrDeclineButton } from "./accident.modals";

import type { AccidentPlan, PlanCost } from "Utils/types";

const ConfirmEnrollmentButton = ({ planID }) => {
  const { id: employeePolicyID } = useSelector(getEmployeeApplication);
  const dependents = useSelector(getDependents);
  const dispatch = useDispatch();
  const [updateEmployeePolicy] = useUpdateEmployeePolicyMutation();
  const [submitting, setSubmitting] = useState(false);

  const confirmEnrollment = async () => {
    setSubmitting(true);
    const dependentIDs = dependents.map((dependent) => dependent.id);
    const { data } = await updateEmployeePolicy({
      id: employeePolicyID,
      isEnrollingAccident: "Yes",
      enrolledAccidentPlanID: planID,
      dependentsEnrollingAccident: dependentIDs.reduce((acc, id) => ({ ...acc, [id]: false }), {}),
    });
    if (data) {
      dispatch(stepCompleted("chooseAccidentPlan"));
      return;
    }
    setSubmitting(false);
  };

  return (
    <Button onClick={confirmEnrollment} disabled={submitting}>
      Continue
    </Button>
  );
};

interface Props {
  plan: AccidentPlan;
  planCost: PlanCost;
}

export const AccidentSinglePlan = ({ plan, planCost }: Props) => {
  const employeeForm = useSelector(getEmployeeForm);
  const breakdown = planCost.pricingType === "age" ? planCost.costBreakdowns.age : planCost.costBreakdowns.eeOnly;
  // Custom logic for CompanyProfile.where(id: [568572, 574192])
  const airFilterCompanyNetSuite = plan.id == "2514080795935434569" || plan.id == "2512380795935434569";
  return (
    <>
      <CardSection>
        <Table>
          <tbody>
            <TR highlightFirstCell={false} withBorder={false}>
              <TD>
                <CarrierLogo plan={plan} maxSize={3} />
              </TD>
              <TD align="left" width="50%">
                <BodyText mt={4}>{plan.display_name}</BodyText>
              </TD>
            </TR>
          </tbody>
        </Table>
      </CardSection>
      <CardSection>
        <BodyText level={6} mb={4}>
          Premium Overview
        </BodyText>
        <Table>
          <thead>
            <HeaderTR>
              <TH>Person</TH>
              <TH align="left" width="50%">
                Your Cost
                {plan.contribution_strategy === "Contributory" && (
                  <InfoPopover
                    infoTitle="Payroll Contributions"
                    infoText="This is the amount you pay each month, taken directly out of your paycheck."
                    placement="top"
                  />
                )}
              </TH>
            </HeaderTR>
          </thead>
          <tbody>
            <TR highlightFirstCell={false} withBorder={false}>
              <TD>{formatNameFL(employeeForm)}</TD>
              <TD align="left">{formatCurrency(breakdown.employeeCost)}</TD>
            </TR>
            {/* Custom logic for  */}
            {airFilterCompanyNetSuite ? (
              <>
                <TR highlightFirstCell={false} withBorder={false}>
                  <TD colSpan={99} align="left">
                    Your employer is covering 100% of your Accident Insurance costs but you may still decline coverage.
                  </TD>
                </TR>
                <TR withBorder={false}>
                  <TD>&nbsp;</TD>
                  <TD align="left">
                    <AccidentEnrollOrDeclineButton plan={plan} planCost={planCost} />
                  </TD>
                </TR>
              </>
            ) : (
              <>
                {plan.contribution_strategy === "Non-Contributory" ? (
                  <>
                    <TR highlightFirstCell={false} withBorder={false}>
                      <TD colSpan={99} align="left">
                        Your employer is covering 100% of your Accident Insurance costs. You will be automatically enrolled.
                      </TD>
                    </TR>
                    <TR withBorder={false}>
                      <TD>&nbsp;</TD>
                      <TD align="left">
                        <ConfirmEnrollmentButton planID={plan.id} />
                      </TD>
                    </TR>
                  </>
                ) : (
                  <TR highlightFirstCell={false} withBorder={false}>
                    <TD>&nbsp;</TD>
                    <TD align="left">
                      <AccidentEnrollOrDeclineButton plan={plan} planCost={planCost} />
                    </TD>
                  </TR>
                )}
              </>
            )}
          </tbody>
        </Table>
      </CardSection>
      <CardSection>
        <BodyText level={6} mb={4}>
          Plan Overview
        </BodyText>
        <Table>
          <thead>
            <HeaderTR>
              <TH>Feature</TH>
              <TH align="left" width="50%">
                Detail
              </TH>
            </HeaderTR>
          </thead>
          <tbody>
            <TR highlightFirstCell={false} withBorder={false}>
              <TD>Plan Type</TD>
              <TD align="left">Accident Insurance</TD>
            </TR>
            {Array.isArray(plan.benefits) && plan.benefits.length > 0 && (
              <TR highlightFirstCell={false} withBorder={false}>
                <TD verticalAlign="top" py={4}>
                  <Span>Other Benefits</Span>
                </TD>
                <TD align="left" pt={4}>
                  <Ul pl={4}>{plan.benefits?.map((benefit, index) => <Li key={index}>{benefit}</Li>)}</Ul>
                </TD>
              </TR>
            )}
          </tbody>
        </Table>
        {plan.has_sbc && (
          <a href={plan.sbc_url} target="_blank" rel="noreferrer">
            <Icon flavor="solid" icon="download" mr={3} />
            View Plan Summary of Benefits and Coverage
          </a>
        )}
      </CardSection>
    </>
  );
};
